import React from 'react';
import {
  Card,
  Grid,
  Group,
  SegmentedControl,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import type { AuditReviewActivityResponse } from '@tinypro-audit/contracts';
import { Link, useLoaderData, useLocation, useNavigate } from 'react-router-dom';

import { AuditReviewAnswerBadge } from '../components/AuditResolutionBadge';
import {
  ANALYTICS_RANGE_OPTIONS,
  getLast7DaysRangeLabel,
  getWeekToDateRangeLabel,
  type AnalyticsRange,
} from './analyticsRanges';

/** Loader data returned by adminAuditReviewStatesLoader(). */
type LoaderData = {
  report: AuditReviewActivityResponse;
  range: AnalyticsRange;
  flaggedCount: number;
};

/** Compact KPI card used across the admin review activity screen. */
function StatCard({ label, value }: { label: string; value: number }): React.ReactElement {
  return (
    <Card withBorder radius="md">
      <Text size="sm" c="dimmed">
        {label}
      </Text>
      <Title order={3}>{value}</Title>
    </Card>
  );
}

/** Format raw audit ids into the same display style used elsewhere in the app. */
function formatJobId(jobId: string): string {
  const trimmed = jobId.trim();
  if (!trimmed) return 'Unknown';
  return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
}

export function AdminAuditReviewStatesPage(): React.ReactElement {
  /** Data provided by the route loader. */
  const { report, range, flaggedCount } = useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const location = useLocation();

  /** Update the URL range param so the loader refetches with the new bucket. */
  function handleRangeChange(next: string): void {
    const params = new URLSearchParams(location.search);
    params.set('range', next);
    navigate({ pathname: location.pathname, search: params.toString() ? `?${params.toString()}` : '' });
  }

  /** Reuse the same hover labels as the main Analytics page. */
  const rangeOptions = React.useMemo(
    () =>
      ANALYTICS_RANGE_OPTIONS.map((opt) =>
        opt.value === 'week-to-date'
          ? {
              ...opt,
              label: (
                <Tooltip label={getWeekToDateRangeLabel()} withArrow>
                  <span>{opt.label}</span>
                </Tooltip>
              ),
            }
          : opt.value === 'last-7-days'
            ? {
                ...opt,
                label: (
                  <Tooltip label={getLast7DaysRangeLabel()} withArrow>
                    <span>{opt.label}</span>
                  </Tooltip>
                ),
              }
            : opt,
      ),
    [],
  );

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={2}>Resolve activity</Title>
          <Text c="dimmed">
            Admin-only summary of jobs reviewed in the selected date range.
          </Text>
        </div>
        <SegmentedControl
          value={range}
          onChange={handleRangeChange}
          data={rangeOptions}
          size="sm"
        />
      </Group>

      <Grid>
        <Grid.Col span={{ base: 12, md: 4, lg: 2 }}>
          <StatCard label="Resolved" value={report.stats.resolvedCount} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4, lg: 2 }}>
          <StatCard label="Flagged" value={flaggedCount} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4, lg: 2 }}>
          <StatCard label="Caught issue: Yes" value={report.stats.caughtJobsiteAlertIssueYesCount} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4, lg: 2 }}>
          <StatCard label="Caught issue: No" value={report.stats.caughtJobsiteAlertIssueNoCount} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4, lg: 2 }}>
          <StatCard
            label="Caught issue: Unanswered"
            value={report.stats.caughtJobsiteAlertIssueUnansweredCount}
          />
        </Grid.Col>
      </Grid>

      <Grid>
        <Grid.Col span={{ base: 12, lg: 4 }}>
          <Card withBorder radius="md">
            <Title order={4} mb="xs">
              Reviewed by user
            </Title>
            <Text size="sm" c="dimmed" mb="sm">
              Counts are grouped by the user who last saved each in-range review row.
            </Text>

            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>User email</Table.Th>
                  <Table.Th ta="right">Reviewed</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {report.byUser.map((row) => (
                  <Table.Tr key={row.userEmail}>
                    <Table.Td>{row.userEmail}</Table.Td>
                    <Table.Td ta="right">{row.reviewedCount}</Table.Td>
                  </Table.Tr>
                ))}

                {report.byUser.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={2}>
                      <Text c="dimmed">No resolve activity found for this range.</Text>
                    </Table.Td>
                  </Table.Tr>
                ) : null}
              </Table.Tbody>
            </Table>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Card withBorder radius="md">
            <Title order={4} mb="xs">
              Review rows
            </Title>
            <Text size="sm" c="dimmed" mb="sm">
              Newest updated rows first.
            </Text>

            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>User email</Table.Th>
                  <Table.Th>Job id</Table.Th>
                  <Table.Th>Caught jobsite issue</Table.Th>
                  <Table.Th>Install issue awareness</Table.Th>
                  <Table.Th>Corrective action</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {report.rows.map((row) => (
                  <Table.Tr key={row.jobId}>
                    <Table.Td>{row.userEmail}</Table.Td>
                    <Table.Td>
                      <Text component={Link} to={`/audits/${row.jobId}`} fw={600}>
                        {formatJobId(row.jobId)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <AuditReviewAnswerBadge value={row.caughtJobsiteAlertIssue} />
                    </Table.Td>
                    <Table.Td>
                      <AuditReviewAnswerBadge value={row.awareOfPotentialInstallationIssue} />
                    </Table.Td>
                    <Table.Td>
                      <AuditReviewAnswerBadge value={row.tookCorrectiveAction} />
                    </Table.Td>
                  </Table.Tr>
                ))}

                {report.rows.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={5}>
                      <Text c="dimmed">No review rows found for this range.</Text>
                    </Table.Td>
                  </Table.Tr>
                ) : null}
              </Table.Tbody>
            </Table>
          </Card>
        </Grid.Col>
      </Grid>
    </Stack>
  );
}
